/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  'use strict';

  /**
   * Bodylux - dual gift campaign coordinator (V2).
   * Extends the tested Stage 9 coordinator, retaining the Focal event hooks,
   * manual-product protection and full-cart refresh behavior.
   *
   * One purchased unit can count toward only ONE campaign. Shared units are
   * allocated to satisfy both campaigns if possible, otherwise Campaign 1
   * has priority unless only Campaign 2 is independently achievable.
   *
   * This script does NOT make a gift free. Shopify discounts must be
   * configured and independently tested at checkout before publication.
   */
  const sectionName = 'bodylux-gift-state';
  const giftMarker = '_bodylux_auto_gift';
  const campaignMarker = '_bodylux_gift_campaign';
  const giftMarkerValue = '1';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let refreshTimer = null;
  let running = false;
  let rerunNeeded = false;
  let currentState = null;

  const isManagedGift = item =>
    Boolean(item && item.properties && String(item.properties[giftMarker]) === giftMarkerValue);

  // Legacy Stage 9 gifts have no campaign ID and belong to Campaign 1.
  function managedCampaign(item) {
    if (!isManagedGift(item)) return 0;
    const value = item.properties[campaignMarker];
    return value == null || value === '' ? 1 : Number(value);
  }

  function nonNegativeInteger(value) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
  }

  function requiredInteger(value) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 1 ? Math.floor(number) : 2;
  }

  function createBanner(target, id) {
    let banner = document.getElementById(id);
    if (banner && banner.nextElementSibling !== target) {
      banner.remove();
      banner = null;
    }
    if (!banner) {
      banner = document.createElement('div');
      banner.id = id;
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.style.cssText = 'border:1px solid #d3dce7;border-left:4px solid #587568;border-radius:6px;padding:12px 14px;margin:12px 0;font-size:14px;line-height:1.45';
      target.parentElement.insertBefore(banner, target);
    }
    return banner;
  }

  function campaignText(campaign, isSingle) {
    const subject = isSingle ? 'månedens gave' : `gavekampagne ${campaign.id}`;
    if (!campaign.qualified) {
      const missing = Math.max(0, campaign.requirement - campaign.progress);
      return `Du har ${campaign.progress} af ${campaign.requirement} kampagneprodukter til ${subject}. Tilføj ${missing} mere for at kvalificere dig.`;
    }
    if (campaign.manualInCart) {
      return `Du opfylder betingelserne for ${subject}. Gaveproduktet ligger allerede i din kurv.`;
    }
    if (campaign.giftInCart) {
      return `Du har kvalificeret dig til ${subject}! Gaven ligger nu i din kurv.`;
    }
    if (currentState.error) {
      return `Du er kvalificeret til ${subject}, men gaven kunne ikke tilføjes lige nu. Opdater kurven.`;
    }
    return `Du har kvalificeret dig til ${subject}! Vi tilføjer gaven til din kurv …`;
  }

  function paint() {
    const elements = [
      { target: document.getElementById('mini-cart-form'), id: 'bodylux-gift-mini-progress' },
      { target: document.querySelector('.cart__content > .line-item-table'), id: 'bodylux-gift-page-progress' }
    ];

    for (const entry of elements) {
      const existing = document.getElementById(entry.id);
      if (!entry.target || !currentState) {
        if (existing) existing.remove();
        continue;
      }

      const campaigns = currentState.campaigns.filter(c => c.enabled);
      const conflict = currentState.conflict;
      const show = conflict || campaigns.some(c => c.progress > 0 || c.qualified);
      if (!show) {
        if (existing) existing.remove();
        continue;
      }

      const banner = createBanner(entry.target, entry.id);
      if (conflict) {
        const message = 'Gavekampagnerne bruger samme gavevariant. Vælg to forskellige gaver under temaindstillingerne, før kampagnerne kan bruges sammen.';
        if (banner.textContent !== message) banner.textContent = message;
        continue;
      }
      const single = campaigns.length === 1 && campaigns[0].id === 1;
      const message = campaigns
        .filter(c => c.progress > 0 || c.qualified)
        .map(c => campaignText(c, single))
        .join(' ');
      // Do not replace identical text nodes: the observer below watches DOM
      // replacements, and unconditional textContent writes would cause a loop.
      if (banner.textContent !== message) banner.textContent = message;
    }
  }

  // Focal can redraw the mini-cart without changing cart contents. Such a
  // redraw removes dynamically inserted status banners after reconcile() ran.
  // Repair only missing/misplaced banners, using the last known cart state.
  // Never make a cart API request or modify an item from this observer.
  function bannerNeedsRepair() {
    if (!currentState) return false;
    const show = currentState.conflict || currentState.campaigns.some(
      campaign => campaign.enabled && (campaign.progress > 0 || campaign.qualified)
    );
    const entries = [
      { target: document.getElementById('mini-cart-form'), id: 'bodylux-gift-mini-progress' },
      { target: document.querySelector('.cart__content > .line-item-table'), id: 'bodylux-gift-page-progress' }
    ];

    return entries.some(({ target, id }) => {
      const banner = document.getElementById(id);
      if (!target || !show) return Boolean(banner);
      return !banner || banner.nextElementSibling !== target;
    });
  }

  function observeCartBanner() {
    if (typeof MutationObserver === 'undefined') return;
    let repairQueued = false;
    const observer = new MutationObserver(() => {
      if (!currentState || repairQueued) return;
      repairQueued = true;
      window.requestAnimationFrame(() => {
        repairQueued = false;
        if (bannerNeedsRepair()) paint();
      });
    });
    // Observe body because Focal sometimes replaces the entire mini-cart's
    // content rather than only replacing the form inside the existing drawer.
    const start = () => {
      if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start, { once: true });
  }

  async function getJson(endpoint, options = {}) {
    const response = await fetch(`${root}${endpoint}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options
    });
    if (!response.ok) {
      throw new Error(`Shopify cart request failed (${response.status}, ${endpoint})`);
    }
    return response.json();
  }

  async function postCart(endpoint, data) {
    return getJson(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    });
  }

  function allocateQualification(raw) {
    const campaigns = raw.campaigns;
    const first = campaigns[0];
    const second = campaigns[1];
    const shared = raw.shared;

    for (const c of campaigns) {
      c.qualified = false;
      c.progress = 0;
      c.manualInCart = false;
      c.giftInCart = false;
    }

    // Identical gift variants cannot be owned/removed independently.
    const conflict = first.enabled && second.enabled && first.giftId === second.giftId;
    if (conflict) return { campaigns, conflict: true, error: false };

    const need1 = first.enabled ? Math.max(0, first.requirement - first.only) : 0;
    const need2 = second.enabled ? Math.max(0, second.requirement - second.only) : 0;
    const possible1 = first.enabled && need1 <= shared;
    const possible2 = second.enabled && need2 <= shared;
    const both = possible1 && possible2 && need1 + need2 <= shared;
    let used1 = 0;
    let used2 = 0;

    if (both) {
      first.qualified = true;
      second.qualified = true;
      used1 = need1;
      used2 = need2;
    } else if (possible1) {
      first.qualified = true;
      used1 = need1;
    } else if (possible2) {
      second.qualified = true;
      used2 = need2;
    }

    const remaining = shared - used1 - used2;
    // Display only unallocated units as progress toward the other campaign.
    first.progress = first.enabled ? Math.min(first.requirement, first.only + used1 + (first.qualified ? 0 : remaining)) : 0;
    second.progress = second.enabled ? Math.min(second.requirement, second.only + used2 + (second.qualified ? 0 : remaining)) : 0;

    return { campaigns, conflict: false, error: false };
  }

  async function readGiftState() {
    const response = await fetch(`${root}cart?section_id=${encodeURIComponent(sectionName)}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Gift-state section failed (${response.status})`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const data = parsed.querySelector('[data-bodylux-gift-state]');
    if (!data) throw new Error('Bodylux gift-state section is missing');
    // Use getAttribute here: HTML dataset does NOT camel-case hyphens followed
    // by digits (data-campaign-1-enabled becomes dataset['campaign-1Enabled']).
    const field = name => data.getAttribute(`data-${name}`);
    // Stop safely rather than acting on a partially migrated section.
    for (const name of ['campaign-1-enabled', 'campaign-2-enabled', 'shared-quantity']) {
      if (field(name) == null) throw new Error(`Gift-state field is missing: ${name}`);
    }

    return allocateQualification({
      campaigns: [
        {
          id: 1,
          enabled: field('campaign-1-enabled') === 'true',
          only: nonNegativeInteger(field('campaign-1-only-quantity')),
          requirement: requiredInteger(field('campaign-1-requirement')),
          giftId: nonNegativeInteger(field('campaign-1-gift-variant-id'))
        },
        {
          id: 2,
          enabled: field('campaign-2-enabled') === 'true',
          only: nonNegativeInteger(field('campaign-2-only-quantity')),
          requirement: requiredInteger(field('campaign-2-requirement')),
          giftId: nonNegativeInteger(field('campaign-2-gift-variant-id'))
        }
      ],
      shared: nonNegativeInteger(field('shared-quantity'))
    });
  }

  function queueUpdate(delay = 180) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(reconcile, delay);
  }

  function signalThemeRefresh() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    window.setTimeout(paint, 350);
    window.setTimeout(paint, 900);
  }

  function isFullCartPage() {
    const cartPath = new URL(`${root}cart`, window.location.origin).pathname.replace(/\/+$/, '');
    const currentPath = window.location.pathname.replace(/\/+$/, '');
    return currentPath === cartPath;
  }

  function refreshAfterGiftMutation() {
    if (isFullCartPage()) {
      window.location.reload();
    } else {
      signalThemeRefresh();
    }
  }

  async function reconcile() {
    if (running) {
      rerunNeeded = true;
      return;
    }
    running = true;
    let mutated = false;

    try {
      // Read cart then Liquid state to avoid deciding against a state from
      // before the most recently observed cart contents.
      const cart = await getJson('cart.js', { headers: { Accept: 'application/json' } });
      const state = await readGiftState();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const managed = items.filter(isManagedGift);
      const desired = new Map(
        state.campaigns.filter(c => c.qualified && !state.conflict && c.giftId).map(c => [c.id, c])
      );

      // A gift deliberately added by the shopper is never deleted or changed
      // by our script. To prevent duplicate products, it supersedes the
      // automatically managed line for that campaign.
      for (const campaign of state.campaigns) {
        campaign.manualInCart = campaign.qualified && items.some(item =>
          Number(item.variant_id) === campaign.giftId && !isManagedGift(item)
        );
      }

      const retained = new Map();
      let change = null;
      for (const item of managed) {
        const campaignId = managedCampaign(item);
        const campaign = desired.get(campaignId);
        if (!campaign || Number(item.variant_id) !== campaign.giftId || campaign.manualInCart || retained.has(campaignId)) {
          change = { id: item.key, quantity: 0 };
          break;
        }
        retained.set(campaignId, item);
        if (Number(item.quantity) !== 1) {
          change = { id: item.key, quantity: 1 };
          break;
        }
      }

      if (change) {
        // Reuse the Stage 9 mutation endpoint; remove only marked items.
        await postCart('cart/change.js', change);
        mutated = true;
      } else {
        const additions = [];
        for (const [id, campaign] of desired) {
          if (!retained.has(id) && !campaign.manualInCart) {
            additions.push({
              id: campaign.giftId,
              quantity: 1,
              properties: {
                [giftMarker]: giftMarkerValue,
                [campaignMarker]: String(id)
              }
            });
          }
        }
        if (additions.length > 0) {
          // Both missing gifts can be added in a single request.
          await postCart('cart/add.js', { items: additions });
          mutated = true;
        }
      }

      // State messages are informational only; Shopify controls final prices.
      for (const campaign of state.campaigns) {
        campaign.giftInCart = campaign.qualified && (
          campaign.manualInCart || retained.has(campaign.id) || (
            mutated && !state.conflict && desired.has(campaign.id)
          )
        );
      }
      currentState = state;
      paint();

      if (mutated) {
        refreshAfterGiftMutation();
        if (!isFullCartPage()) rerunNeeded = true;
      } else {
        window.setTimeout(paint, 350);
        window.setTimeout(paint, 900);
      }
    } catch (error) {
      // If state or a cart operation fails, never change a shopper's own lines.
      console.warn('Bodylux giveaway:', error.message);
      if (currentState) {
        currentState.error = true;
        paint();
      }
    } finally {
      running = false;
      if (rerunNeeded) {
        rerunNeeded = false;
        queueUpdate(280);
      }
    }
  }

  // Keep informational messages visible across theme-controlled cart redraws.
  observeCartBanner();

  document.addEventListener('cart:updated', () => queueUpdate());
  document.addEventListener('variant:added', () => queueUpdate());
  document.addEventListener('cart:refresh', () => queueUpdate(400));
  document.addEventListener('shopify:section:load', () => queueUpdate());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => queueUpdate(0), { once: true });
  } else {
    queueUpdate(0);
  }
})();
